// 報表 — sales, gross profit, payment mix (= cash drawer), grooming vs retail,
// best sellers, by hour / by day, channel, plus a CSV export. Grooming
// includes 收款 taken on appointments outside the POS (pos_sales_report).
import { zhEn, dateLocale } from "./posTier.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { C, Btn, Chip, Card, Label, Empty, money, round2, useWide } from "./posUi.jsx";
import { posErrorText } from "./posMath.js";
import { methodLabel, payLabel } from "./posReceipt.js";
import { rangeIso } from "./History.jsx";
import { fetchSales, fetchSalesReport } from "../../lib/api.js";

export default function Reports({ has = () => true, tenant, sym, customerById, refreshKey, products = [] }) {
  const wide = useWide(900);
  const [range, setRange] = useState("today");
  const [sales, setSales] = useState(null);
  const [srv, setSrv] = useState(null);   // server-summed report (pos_sales_report)
  const [err, setErr] = useState("");
  const byHourRange = range === "today" || range === "yesterday";
  const load = useCallback(async () => {
    setErr(""); setSrv(null);
    const { from, to } = rangeIso(range);
    // Totals are summed in SQL (no row cap); fall back to summing on the
    // device if the report function isn't installed yet.
    try { setSrv(await fetchSalesReport({ tenantId: tenant.id, from, to, bucket: byHourRange ? "hour" : "day" })); setSales([]); return; }
    catch (e) { if (!/pos_sales_report|function|PGRST202|404/i.test(String(e?.message))) { setErr(posErrorText(e)); } }
    try { setSales(await fetchSales(tenant.id, { from, to, limit: 5000 })); }
    catch (e) { setErr(posErrorText(e)); setSales([]); }
  }, [tenant.id, range, byHourRange]);
  useEffect(() => { setSales(null); setSrv(null); load(); }, [load, refreshKey]);

  const rLocal = useMemo(() => {
    const out = { revenue: 0, grooming: 0, retail: 0, topup: 0, refunds: 0, orders: 0, cost: 0, retailNet: 0, discounts: 0, pays: {}, products: {}, buckets: {}, channels: {} };
    const byHour = range === "today" || range === "yesterday";
    for (const s of sales || []) {
      if (s.kind === "sale" || (s.kind === "exchange" && Number(s.total) > 0)) out.orders += s.kind === "sale" ? 1 : 0;
      if (Number(s.total) < 0) out.refunds += -Number(s.total);
      const od = Number(s.order_discount) || 0;
      out.discounts += od;
      let saleRev = -od;
      for (const l of s.sale_lines || []) {
        const t = Number(l.total) || 0;
        if (l.kind === "wallet_topup") { out.topup += t; continue; }
        saleRev += t;
        if (l.kind === "appointment") out.grooming += t;
        else {
          out.retail += t;
          out.cost += Number(l.cost) || 0;
          const key = l.product_id || "c:" + l.name;
          const p = out.products[key] || (out.products[key] = { name: l.name, qty: 0, rev: 0 });
          p.qty += Number(l.qty) || 0; p.rev += t;
        }
        out.discounts += Math.max(0, Number(l.discount) || 0);
      }
      out.retail -= od;
      out.revenue += saleRev;
      for (const p of s.sale_payments || []) {
        if (p.method === "exchange_credit") continue;
        const k = methodLabel(p);
        out.pays[k] = round2((out.pays[k] || 0) + Number(p.amount));
      }
      const d = new Date(s.created_at);
      const b = byHour ? String(d.getHours()).padStart(2, "0") + ":00" : (d.getMonth() + 1) + "/" + d.getDate();
      out.buckets[b] = (out.buckets[b] || 0) + saleRev;
      const ch = s.source === "pos" ? zhEn("🏪 門市", "🏪 In store") : zhEn("🌐 網店", "🌐 Online");
      out.channels[ch] = (out.channels[ch] || 0) + saleRev;
    }
    out.retailNet = out.retail;
    out.gross = out.retail - out.cost;
    out.avg = out.orders ? out.revenue / out.orders : 0;
    out.topProducts = Object.values(out.products).filter(p => p.qty > 0).sort((a, b) => b.rev - a.rev).slice(0, 10);
    const keys = Object.keys(out.buckets);
    out.series = (byHour ? keys.sort() : keys.sort((a, b) => { const [am, ad] = a.split("/").map(Number), [bm, bd] = b.split("/").map(Number); return am - bm || ad - bd; })).map(k => [k, out.buckets[k]]);
    return out;
  }, [sales, range]);
  const r = useMemo(() => {
    if (!srv) return rLocal;
    const n = (x) => Number(x) || 0;
    const fmtK = (k) => byHourRange ? k : (() => { const [, m, d] = k.split("-"); return Number(m) + "/" + Number(d); })();
    return {
      revenue: n(srv.revenue), grooming: n(srv.grooming), groomingOutside: n(srv.groomingOutside), retailNet: n(srv.retail), topup: n(srv.topup), refunds: n(srv.refunds),
      orders: n(srv.orders), discounts: n(srv.discounts), gross: n(srv.retail) - n(srv.retailCost),
      avg: n(srv.orders) ? n(srv.revenue) / n(srv.orders) : 0,
      pays: Object.fromEntries(Object.entries(srv.pays || {}).map(([k, v]) => [({ cash: zhEn("現金", "Cash"), wallet: zhEn("錢包", "Wallet"), points: zhEn("積分", "Points") })[k] || k, n(v)])),
      topProducts: (srv.products || []).filter(x => n(x.qty) > 0).slice(0, 10).map(x => ({ name: x.name, qty: n(x.qty), rev: n(x.rev) })),
      series: (srv.series || []).map(x => [fmtK(x.k), n(x.v)]),
      channels: Object.fromEntries(Object.entries(srv.channels || {}).map(([k, v]) => [k === "pos" ? zhEn("🏪 門市", "🏪 In store") : k === "appointment" ? zhEn("✂️ 預約收款", "✂️ Booking payments") : zhEn("🌐 網店", "🌐 Online"), n(v)])),
      categories: srv.categories || [], brands: srv.brands || [], staff: srv.staff || [], promos: srv.promos || [],
      purchases: n(srv.purchases), purchaseCount: n(srv.purchaseCount),
    };
  }, [srv, rLocal, byHourRange]);
  // Stock on hand (now, not the period): sellable leaves with tracked stock.
  const stockValue = useMemo(() => {
    const parents = new Set(products.filter(p => p.parentId).map(p => p.parentId));
    let cost = 0, retail = 0, units = 0;
    for (const p of products) if (p.trackStock && !parents.has(p.id) && p.stock > 0) { cost += p.stock * p.cost; retail += p.stock * p.price; units += p.stock; }
    return { cost, retail, units };
  }, [products]);

  const exportCsv = async () => {
    // Line-level export needs the rows themselves (fetched on demand).
    let list = sales && sales.length ? sales : null;
    if (!list) { try { list = await fetchSales(tenant.id, { ...rangeIso(range), limit: 5000 }); } catch (e) { setErr(posErrorText(e)); return; } }
    const rows = [[zhEn("單號", "Receipt"), zhEn("時間", "Time"), zhEn("類型", "Type"), zhEn("渠道", "Channel"), zhEn("客戶", "Customer"), zhEn("項目", "Items"), zhEn("數量", "Qty"), zhEn("單價", "Unit price"), zhEn("折扣", "Discount"), zhEn("小計", "Subtotal"), zhEn("成本", "Cost"), zhEn("付款方式", "Payment method")]];
    for (const s of list) {
      const pays = (s.sale_payments || []).map(p => methodLabel(p) + " " + p.amount).join(" / ");
      for (const l of s.sale_lines || []) rows.push([s.number, new Date(s.created_at).toLocaleString(dateLocale()), s.kind, s.source, customerById(s.customer_id)?.name || "", l.name, l.qty, l.unit_price, l.discount, l.total, l.cost, pays]);
      if (Number(s.order_discount)) rows.push([s.number, "", "", "", "", zhEn("整單折扣", "Order discount"), "", "", "", -Number(s.order_discount), "", ""]);
    }
    const csv = "﻿" + rows.map(r => r.map(v => { const x = String(v ?? ""); return /[",\n]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x; }).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
    a.download = "pos-sales-" + range + "-" + new Date().toISOString().slice(0, 10) + ".csv";
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  const maxBucket = Math.max(1, ...r.series.map(([, v]) => v));
  const payTotal = Object.values(r.pays).reduce((s, v) => s + v, 0);

  return (
    <div>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}>
        <div className="pos-noscroll" style={{ display: "flex", gap: 6, overflowX: "auto", flex: 1 }}>
          {[["today", zhEn("今日", "Today")], ["yesterday", zhEn("尋日", "Yesterday")], ["d7", zhEn("7 日", "7 days")], ["month", zhEn("本月", "This month")], ["d90", zhEn("90 日", "90 days")]].map(([k, l]) => {
            const locked = !has("standard") && k !== "today" && k !== "yesterday";
            return <Chip key={k} on={range === k} onClick={() => locked ? window.alert(zhEn("基本版報表只睇今日同尋日 — 升級專業版睇 7 日、本月、90 日同匯出 CSV。", "The Basic plan shows today and yesterday only — upgrade to Pro for 7 days, this month, 90 days and CSV export.")) : setRange(k)}>{l}{locked ? " 🔒" : ""}</Chip>;
          })}
        </div>
        <button type="button" onClick={load} aria-label={zhEn("重新整理", "Refresh")} style={{ width: 40, height: 36, borderRadius: 12, border: "1.5px solid " + C.line, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.ink }}><RefreshCw size={16} /></button>
      </div>
      {err && <p style={{ color: C.red, fontWeight: 800 }}>{err}</p>}
      {sales === null && !srv ? <p style={{ textAlign: "center", color: C.sub, fontWeight: 800, padding: 30 }}>{zhEn("載入中…", "Loading…")}</p>
        : !srv && sales.length === 0 ? <Empty icon="📊" title={zhEn("呢段時間未有銷售", "No sales in this period")} />
        : (
          <>
            <Card style={{ padding: 18, marginBottom: 10, background: "linear-gradient(135deg,#FF9A9E,#FF758F)", border: "none" }}>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.85)", fontWeight: 900, fontSize: 13 }}>{zhEn("營業額（已扣退款）", "Takings (after refunds)")}</p>
              <p style={{ margin: "2px 0 6px", color: "#fff", fontWeight: 900, fontSize: 36, letterSpacing: -1 }}>{money(r.revenue, sym)}</p>
              <p style={{ margin: 0, color: "rgba(255,255,255,0.9)", fontWeight: 800, fontSize: 13 }}>{r.orders} {zhEn("單 · 平均", "sales · average")} {money(r.avg, sym)}{r.refunds ? zhEn(" · 退款 ", " · refunds ") + money(r.refunds, sym) : ""}</p>
            </Card>
            <div style={{ display: "grid", gridTemplateColumns: wide ? "repeat(4,1fr)" : "1fr 1fr", gap: 8, marginBottom: 12 }}>
              <Kpi label={zhEn("✂️ 美容", "✂️ Grooming")} v={money(r.grooming, sym)} sub={r.groomingOutside ? zhEn("含預約收款 ", "Incl. booking payments ") + money(r.groomingOutside, sym) : ""} />
              <Kpi label={zhEn("🛍️ 零售", "🛍️ Retail")} v={money(r.retailNet, sym)} />
              {has("full") && <Kpi label={zhEn("零售毛利", "Retail margin")} v={money(r.gross, sym)} sub={r.retailNet > 0 ? Math.round(r.gross / r.retailNet * 100) + "%" : ""} tone={r.gross >= 0 ? C.green : C.red} />}
              <Kpi label={zhEn("💳 儲值收入", "💳 Top-up income")} v={money(r.topup, sym)} sub={zhEn("唔計入營業額", "Not counted in takings")} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: wide ? "1fr 1fr" : "1fr", gap: 10 }}>
              <Card style={{ padding: 14 }}>
                <Label>{zhEn("收款方式（錢櫃對數）", "Payment methods (till reconciliation)")}</Label>
                {Object.entries(r.pays).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                  <Bar key={k} label={payLabel(k)} value={v} max={Math.max(...Object.values(r.pays))} text={money(v, sym)} color={k === "現金" ? C.green : undefined} />
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 900, color: C.ink, borderTop: "1px dashed " + C.line, paddingTop: 8, marginTop: 4 }}><span>{zhEn("合計", "Total")}</span><span>{money(payTotal, sym)}</span></div>
              </Card>
              <Card style={{ padding: 14 }}>
                <Label>{range === "today" || range === "yesterday" ? zhEn("每小時", "Per hour") : zhEn("每日", "Per day")}</Label>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 4, height: 140, paddingTop: 8 }}>
                  {r.series.map(([k, v]) => (
                    <div key={k} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, minWidth: 0 }}>
                      <div title={money(v, sym)} style={{ width: "100%", maxWidth: 28, height: Math.max(3, v / maxBucket * 110), borderRadius: 8, background: C.grad }} />
                      <span style={{ fontSize: 10, fontWeight: 800, color: C.sub, whiteSpace: "nowrap" }}>{k}</span>
                    </div>
                  ))}
                </div>
              </Card>
              <Card style={{ padding: 14 }}>
                <Label>{zhEn("熱賣商品", "Top products")}</Label>
                {r.topProducts.length === 0 ? <p style={{ color: C.sub, fontWeight: 800, fontSize: 13 }}>{zhEn("未有零售", "No retail sales yet")}</p>
                  : r.topProducts.map((p, i) => (
                    <div key={p.name + i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 0", borderBottom: "1px solid " + C.line }}>
                      <span style={{ width: 22, fontWeight: 900, color: i < 3 ? C.accent : C.faint }}>{i + 1}</span>
                      <span style={{ flex: 1, fontWeight: 800, color: C.ink, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                      <span style={{ fontWeight: 800, color: C.sub, fontSize: 13 }}>×{p.qty}</span>
                      <span style={{ fontWeight: 900, color: C.ink, fontSize: 14, minWidth: 70, textAlign: "right" }}>{money(p.rev, sym)}</span>
                    </div>
                  ))}
              </Card>
              <Card style={{ padding: 14 }}>
                <Label>{zhEn("渠道 · 折扣", "Channels · discounts")}</Label>
                {Object.entries(r.channels).map(([k, v]) => <Bar key={k} label={k} value={v} max={Math.max(...Object.values(r.channels), 1)} text={money(v, sym)} />)}
                <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 800, color: C.sub, fontSize: 13, marginTop: 8 }}><span>{zhEn("折扣總額", "Total discounts")}</span><span>{money(r.discounts, sym)}</span></div>
              </Card>
              {has("full") && r.categories?.length > 0 && (
                <Card style={{ padding: 14 }}>
                  <Label>{zhEn("零售 · 按分類", "Retail · by category")}</Label>
                  {r.categories.slice(0, 10).map(c => <Bar key={c.name} label={c.name + " · " + Number(c.qty) + zhEn(" 件", " items") + (Number(c.rev) > 0 && c.cost != null ? zhEn(" · 毛利 ", " · margin ") + Math.round((1 - Number(c.cost) / Number(c.rev)) * 100) + "%" : "")} value={Number(c.rev)} max={Math.max(...r.categories.map(x => Number(x.rev)), 1)} text={money(c.rev, sym)} />)}
                </Card>
              )}
              {has("full") && r.brands?.length > 0 && (
                <Card style={{ padding: 14 }}>
                  <Label>{zhEn("零售 · 按品牌", "Retail · by brand")}</Label>
                  {r.brands.slice(0, 8).map(b => <Bar key={b.name} label={b.name + " · " + Number(b.qty) + zhEn(" 件", " items")} value={Number(b.rev)} max={Math.max(...r.brands.map(x => Number(x.rev)), 1)} text={money(b.rev, sym)} />)}
                </Card>
              )}
              {has("full") && r.staff?.length > 0 && (
                <Card style={{ padding: 14 }}>
                  <Label>{zhEn("員工", "Staff")}</Label>
                  {r.staff.map(st => (
                    <div key={st.name} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.line, fontWeight: 800, fontSize: 14, color: C.ink }}>
                      <span>{st.name}</span><span style={{ color: C.sub }}>{Number(st.orders)} {zhEn("單 ·", "sales ·")} <b style={{ color: C.ink }}>{money(st.rev, sym)}</b></span>
                    </div>
                  ))}
                </Card>
              )}
              {r.promos?.length > 0 && (
                <Card style={{ padding: 14 }}>
                  <Label>{zhEn("優惠", "Promotions")}</Label>
                  {r.promos.map(pr => (
                    <div key={pr.name} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.line, fontWeight: 800, fontSize: 14, color: C.ink }}>
                      <span>🎉 {pr.name}</span><span style={{ color: C.sub }}>{Number(pr.lines)} {zhEn("項 ·", "items ·")} <b style={{ color: C.red }}>−{money(pr.discount, sym)}</b></span>
                    </div>
                  ))}
                </Card>
              )}
              {has("standard") && <Card style={{ padding: 14 }}>
                <Label>{zhEn("存貨 · 採購", "Stock · purchasing")}</Label>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.line, fontWeight: 800, fontSize: 14, color: C.ink }}><span>{zhEn("而家存貨（成本）", "Stock now (cost)")}</span><span>{money(stockValue.cost, sym)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.line, fontWeight: 800, fontSize: 14, color: C.ink }}><span>{zhEn("而家存貨（售價）", "Stock now (retail)")}</span><span>{money(stockValue.retail, sym)}</span></div>
                <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid " + C.line, fontWeight: 800, fontSize: 14, color: C.sub }}><span>{zhEn("件數", "Units")}</span><span>{Math.round(stockValue.units)}</span></div>
                {r.purchases != null && <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", fontWeight: 800, fontSize: 14, color: C.ink }}><span>{zhEn("期內到貨採購（", "Purchases received in the period (")}{r.purchaseCount || 0} {zhEn("張）", "orders)")}</span><span>{money(r.purchases, sym)}</span></div>}
              </Card>}
            </div>
            {has("standard") && <Btn kind="ghost" style={{ width: "100%", marginTop: 12 }} onClick={exportCsv}><Download size={18} /> {zhEn("匯出 CSV（Excel 可開）", "Export CSV (opens in Excel)")}</Btn>}
          </>
        )}
    </div>
  );
}

const Kpi = ({ label, v, sub, tone }) => (
  <Card style={{ padding: "12px 14px" }}>
    <p style={{ margin: 0, fontSize: 12, fontWeight: 900, color: C.sub }}>{label}</p>
    <p style={{ margin: "2px 0 0", fontSize: 20, fontWeight: 900, color: tone || C.ink, letterSpacing: -0.5 }}>{v}</p>
    {sub && <p style={{ margin: 0, fontSize: 11.5, fontWeight: 700, color: C.sub }}>{sub}</p>}
  </Card>
);
const Bar = ({ label, value, max, text, color }) => (
  <div style={{ marginBottom: 8 }}>
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, fontWeight: 800, color: C.ink, marginBottom: 3 }}><span>{label}</span><span>{text}</span></div>
    <div style={{ height: 8, borderRadius: 99, background: "#F3F2F8", overflow: "hidden" }}>
      <div style={{ height: "100%", width: Math.max(2, Math.abs(value) / (max || 1) * 100) + "%", background: color || C.grad, borderRadius: 99 }} />
    </div>
  </div>
);
